import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { AddonEntry, ItemSpec, OptionAddons, OptionGroup, ProductOptions } from '@/types'

/**
 * The Product Options groups (GET /option-groups, public): label, order,
 * display and pictures for every garment option. The product form, the
 * configurator, checkout pricing and every page that prints an order's options
 * read this one list. Hidden groups are included, because an older order may
 * still carry one; use activeGroups() for anything somebody picks from.
 */
export const optionGroupsQuery = {
  queryKey: ['option-groups'],
  queryFn: async () => (await api.get<{ data: OptionGroup[] }>('/option-groups')).data.data,
}

export function useOptionGroups() {
  return useQuery(optionGroupsQuery)
}

/** The groups offered on products and new orders right now, in admin order. */
export function activeGroups(groups: OptionGroup[] | undefined): OptionGroup[] {
  return (groups ?? []).filter((group) => group.active)
}

/** A product's value list for one group key; anything that is not a list reads as none. */
export function offeredList(options: ProductOptions | undefined, key: string): string[] {
  const values = options?.[key]
  return Array.isArray(values) ? values.filter((value): value is string => typeof value === 'string') : []
}

/**
 * The values a product offers in a group, in the group's own order. A value the
 * group no longer lists keeps its place at the end, so nothing the product
 * holds silently disappears from the picker.
 */
export function offeredValues(options: ProductOptions | undefined, group: OptionGroup): string[] {
  const offered = offeredList(options, group.key)
  const known = group.values.map((value) => value.value).filter((value) => offered.includes(value))

  return [...known, ...offered.filter((value) => !known.includes(value))]
}

/**
 * The keys of the groups a product insists on: every group it offers values in,
 * minus the ones the admin switched off for it (options.optional_options).
 * Visibility is not decided here: missingRequired checks it at the moment of
 * asking, as OrderService::validateSpec does.
 */
export function requiredKeys(options: ProductOptions | undefined): string[] {
  const optional = offeredList(options, 'optional_options')

  return Object.keys(options ?? {}).filter(
    (key) => key !== 'optional_options' && offeredList(options, key).length > 0 && !optional.includes(key),
  )
}

/**
 * The visible groups among `keys` that the spec leaves empty, in admin order.
 * Term for term with OrderService::validateSpec. Reads as nothing missing until
 * the groups load, so the server stays the gate.
 */
export function missingRequired(spec: ItemSpec, keys: string[] | undefined, groups: OptionGroup[] | undefined): OptionGroup[] {
  if (!keys?.length) return []
  const skipped = skippedKeys(spec, groups)

  return activeGroups(groups).filter((group) => keys.includes(group.key) && !skipped.includes(group.key) && !spec[group.spec_key])
}

/**
 * The group keys the spec's choices make unnecessary (Muslimah skips Sleeve
 * type, Duncan 2026-11-03). Client copy of OptionCatalogService::skippedKeys:
 * the server neither asks for nor stores a skipped group.
 */
export function skippedKeys(spec: ItemSpec | null | undefined, groups: OptionGroup[] | undefined): string[] {
  const skipped = new Set<string>()

  for (const group of activeGroups(groups)) {
    const chosen = spec?.[group.spec_key]
    if (!chosen) continue
    for (const key of group.values.find((value) => value.value === chosen)?.skips ?? []) {
      if (key !== group.key) skipped.add(key)
    }
  }

  return [...skipped]
}

/** The spec without the choices a skipped group still holds, e.g. a sleeve picked before Muslimah. */
export function withoutSkipped(spec: ItemSpec, groups: OptionGroup[] | undefined): ItemSpec {
  const skipped = skippedKeys(spec, groups)
  if (skipped.length === 0) return spec

  const next = { ...spec }
  for (const group of activeGroups(groups)) {
    if (skipped.includes(group.key)) delete next[group.spec_key]
  }

  return next
}

/** "Collar type", "Collar type and Material (fabric)", "Collar type, Sleeve type and Cutting". */
export function groupList(groups: OptionGroup[]): string {
  const labels = groups.map((group) => group.label)
  if (labels.length <= 1) return labels[0] ?? ''

  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * Per-piece add-on total for the spec's choices. Walks the same visible groups
 * as the server's PricingService::optionAddons, term for term, and is display
 * only - the server recomputes at placement. Reads as 0 until the groups load.
 */
export function specAddonTotal(
  addons: OptionAddons | undefined,
  spec: ItemSpec,
  groups: OptionGroup[] | undefined,
  tierId?: number | null,
): number {
  if (!addons) return 0
  // A skipped group is dropped at placement, so it is never charged.
  const skipped = skippedKeys(spec, groups)

  return activeGroups(groups).reduce(
    (sum, group) => (skipped.includes(group.key) ? sum : sum + addonAmount(addons[group.key]?.[spec[group.spec_key] ?? ''], tierId)),
    0,
  )
}

/**
 * One value's add-on for a tier. A plain number (the storefront's figures,
 * already resolved for the viewer, or an older product) is every tier's; a
 * {tier_id: amount} map (admin and sales payloads) needs the tier, and reads
 * 0 without one. A save fills every tier, so a tier missing from the map is
 * rare; the server then falls back to the highest-ranked tier listed.
 */
export function addonAmount(entry: AddonEntry | undefined, tierId?: number | null): number {
  const amount = typeof entry === 'number' ? entry : tierId != null ? entry?.[tierId] : undefined
  return typeof amount === 'number' && Number.isFinite(amount) && amount > 0 ? amount : 0
}

/** "POLO BUTTON (+RM2.00)" when the value carries an add-on, else just the value. */
export function addonLabel(value: string, amount?: number): string {
  return amount && amount > 0 ? `${value} (+RM${amount.toFixed(2)})` : value
}

export const FREE_TEXT_FIELDS: { key: string; label: string }[] = [
  { key: 'neck_color', label: 'Neck color' },
  { key: 'piping', label: 'Piping' },
  { key: 'remark', label: 'Remark' },
]

/**
 * The spec keys no option group owns. StoreOrderRequest validates these three
 * by name whatever the group list looks like, so they always carry.
 */
export const FREE_TEXT_SPEC_KEYS: string[] = FREE_TEXT_FIELDS.map((field) => field.key)

export interface SpecEntry {
  key: string
  label: string
  value: string
  /** The chosen value's picture from Product Options, whatever the group's display. */
  image?: string | null
}

/**
 * An item's options as labelled entries: every Product Options group in admin
 * order (hidden ones too, since an older order may carry one), then any spec
 * key no group knows any more under a label made from the key, then the free
 * text fields. `omit` drops spec keys a compact view leaves out.
 */
export function specEntries(spec: ItemSpec | null | undefined, groups: OptionGroup[] | undefined, omit: string[] = []): SpecEntry[] {
  if (!spec) return []

  const entries: SpecEntry[] = []
  const known = new Set(FREE_TEXT_FIELDS.map((field) => field.key))

  for (const group of groups ?? []) {
    known.add(group.spec_key)
    const value = spec[group.spec_key]
    if (value) {
      const image = group.values.find((option) => option.value === value)?.image_path
      entries.push({ key: group.spec_key, label: group.label, value, image })
    }
  }

  for (const [key, value] of Object.entries(spec)) {
    if (!known.has(key) && value) entries.push({ key, label: readableKey(key), value })
  }

  for (const field of FREE_TEXT_FIELDS) {
    const value = spec[field.key]
    if (value) entries.push({ key: field.key, label: field.label, value })
  }

  return entries.filter((entry) => !omit.includes(entry.key))
}

/** [label, value] lines for the order and job pages. */
export function specLines(spec: ItemSpec | null | undefined, groups: OptionGroup[] | undefined): [string, string][] {
  return specEntries(spec, groups).map((entry) => [entry.label, entry.value])
}

/**
 * The compact " / " line on the cart and the customer's order page. The
 * printed job sheet uses specEntries instead, one box per option. Cutting reads "RAGLAN CUT", where a bare "NORMAL" would
 * not say what it is.
 */
export function specSummary(spec: ItemSpec | null | undefined, groups: OptionGroup[] | undefined, omit: string[] = []): string {
  return specEntries(spec, groups, omit)
    .map((entry) => (entry.key === 'cutting' ? `${entry.value} CUT` : entry.value))
    .join(' / ')
}

/** "pocket_type" reads "Pocket type". */
function readableKey(key: string): string {
  const words = key.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}
